import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { BannerMediaLayer } from '@/components/ui/BannerMedia';
import { withAlpha } from '@/components/ui/BannerTextColorControl';

export interface AdSlide {
    id: string;
    title: string;
    subtitle: string;
    cta: string;
    icon: React.ReactNode;
    gradient: string;
    page: string;
    category?: string;
}

interface AdSheetModalProps {
    slides: AdSlide[];
    isOpen: boolean;
    startIndex: number;
    onClose: () => void;
    onNavigate: (page: string, category?: string) => void;
    /** system_configuration key that holds the media for a given slide */
    bgKeyFor: (index: number) => string;
    /** resolved text colour chosen by the admin for a given slide */
    textColorFor: (index: number) => string;
    titleFor: (slide: AdSlide) => string;
    subtitleFor: (slide: AdSlide) => string;
    ctaFor: (slide: AdSlide) => string;
}

const STEP_MS = 4200;
const TICK_MS = 40;

/**
 * Full-screen ad viewer — same bottom-sheet language as the staking rules modal:
 * drag-down to dismiss, an animated capsule progress bar, and a single action pill.
 */
export const AdSheetModal: React.FC<AdSheetModalProps> = ({
    slides, isOpen, startIndex, onClose, onNavigate, bgKeyFor, textColorFor, titleFor, subtitleFor, ctaFor
}) => {
    const { impactOccurred } = useHapticFeedback();
    const [index, setIndex] = useState(startIndex);
    const [progress, setProgress] = useState(0);
    const [direction, setDirection] = useState(1);

    useEffect(() => {
        if (!isOpen) return;
        setIndex(startIndex);
        setDirection(1);
        setProgress(0);
    }, [isOpen, startIndex]);

    useEffect(() => {
        if (!isOpen) return;
        const step = 100 / (STEP_MS / TICK_MS);
        const timer = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    setDirection(1);
                    setIndex(i => (i + 1) % slides.length);
                    return 0;
                }
                return prev + step;
            });
        }, TICK_MS);
        return () => clearInterval(timer);
    }, [isOpen, slides.length]);

    const jumpTo = (target: number) => {
        if (target === index) return;
        impactOccurred('light');
        setDirection(target > index ? 1 : -1);
        setIndex(target);
        setProgress(0);
    };

    const handleDragEnd = (_: any, info: any) => {
        if (info.offset.y > 20 || info.velocity.y > 50) {
            impactOccurred('light');
            onClose();
        }
    };

    const slideVariants = {
        enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (dir: number) => ({ x: dir < 0 ? 60 : -60, opacity: 0 }),
    };

    const current = slides[index];
    if (!isOpen || !current) return null;

    const textColor = textColorFor(index);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-xl flex items-end sm:items-center justify-center">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.15 } }}
                        onClick={onClose}
                        className="absolute inset-0"
                    />

                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%', transition: { duration: 0.18, ease: [0.32, 0, 0.67, 0] } }}
                        transition={{ type: 'spring', damping: 24, stiffness: 420, mass: 0.4 }}
                        drag="y"
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={{ top: 0, bottom: 1 }}
                        dragSnapToOrigin
                        onDragEnd={handleDragEnd}
                        className="w-full max-w-md bg-tg-secondary-bg rounded-t-[36px] px-4 pt-3 pb-7 shadow-2xl relative z-10 overflow-hidden flex flex-col items-center touch-pan-y"
                    >
                        <div
                            onClick={() => { impactOccurred('light'); onClose(); }}
                            className="w-12 h-1.5 rounded-full bg-white/20 mx-auto mb-4 cursor-pointer hover:bg-white/40 active:scale-95 transition-all"
                        />

                        {/* ─── Media stage ─── */}
                        <div className="w-full h-56 rounded-[28px] relative overflow-hidden mb-5 shadow-inner">
                            <AnimatePresence mode="wait" custom={direction}>
                                <motion.div
                                    key={current.id}
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.25 }}
                                    className="absolute inset-0"
                                >
                                    <BannerMediaLayer bgKey={bgKeyFor(index)} className="!inset-0" />
                                    <div className={`absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent bg-gradient-to-r ${current.gradient} opacity-70`} />
                                    <div className="absolute inset-x-0 bottom-0 p-4 flex items-center gap-3">
                                        <div className="w-11 h-11 shrink-0 rounded-2xl glass flex items-center justify-center">
                                            {current.icon}
                                        </div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-white/60">
                                            {String(index + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                                        </p>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* ─── Title & subtitle ─── */}
                        <div className="w-full text-center mb-5 px-2">
                            <AnimatePresence mode="wait" custom={direction}>
                                <motion.div
                                    key={current.id}
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.22 }}
                                >
                                    <h2 className="text-[19px] font-black tracking-tight leading-tight" style={{ color: textColor }}>
                                        {titleFor(current)}
                                    </h2>
                                    <p className="text-[12px] font-medium leading-relaxed mt-1.5" style={{ color: withAlpha(textColor, 0.75) }}>
                                        {subtitleFor(current)}
                                    </p>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* ─── Animated capsule dots (fill, then flip) ─── */}
                        <div className="flex items-center justify-center gap-1.5 mb-6">
                            {slides.map((s, i) => {
                                const isActive = i === index;
                                return (
                                    <div
                                        key={s.id}
                                        onClick={() => jumpTo(i)}
                                        className={`h-1.5 rounded-full cursor-pointer transition-all duration-300 relative overflow-hidden ${
                                            isActive ? 'w-6 bg-white/20' : 'w-1.5 bg-white/20 hover:bg-white/40'
                                        }`}
                                    >
                                        {isActive && (
                                            <motion.div
                                                className="h-full bg-white rounded-full"
                                                style={{ width: `${progress}%` }}
                                                transition={{ ease: 'linear' }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* ─── Action pill ─── */}
                        <button
                            onClick={() => {
                                impactOccurred('medium');
                                onClose();
                                onNavigate(current.page, current.category);
                            }}
                            className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#3390EC] to-[#007AFF] text-white text-sm font-black tracking-wide flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all shadow-lg shadow-[#3390EC]/25"
                        >
                            {ctaFor(current)}
                            <ChevronRight size={15} className="rtl-flip" />
                        </button>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default AdSheetModal;